import { useParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Button, Flex, Modal, Stack, Text } from '@mantine/core';

import { usePatchBoardUser } from 'api/boards';
import { CheckboxField, Form, UserAvatar } from 'components/shared';
import { useShowNotification } from 'hooks';
import type { BoardUserType } from 'types';
import { useEffect } from 'react';

type Props = {
  opened: boolean;
  onClose: () => void;
  user: BoardUserType;
};

type FormData = {
  permissions: BoardUserType['permissions'];
};

export const MemberPermissionsModal = ({ opened, onClose, user }: Props) => {
  const { organisationId, boardId } = useParams() as {
    organisationId: string;
    boardId: string;
  };
  const userLabel =
    user.name && user.surname ? `${user.name} ${user.surname}` : user.email;

  const form = useForm<FormData>({
    defaultValues: { permissions: user.permissions },
  });
  const { mutateAsync: patchBoardUser } = usePatchBoardUser();
  const { t } = useTranslation('organisations');
  const showNotification = useShowNotification();

  const onSubmit = async (data: FormData) => {
    try {
      await patchBoardUser({
        pathParams: { organisationId, boardId, boardUserId: user.id },
        body: data,
      });
      showNotification({
        variant: 'success',
        message: t(
          'organisationView.boards.members.permissionsModal.successMessage',
        ),
      });
      onClose();
    } catch (error) {
      showNotification({
        variant: 'error',
        message: t(
          'organisationView.boards.members.permissionsModal.errorMessage',
        ),
      });
    }
  };

  useEffect(() => {
    form.reset({ permissions: user.permissions });
  }, [user]);

  const canManageAllDocsEnabled = form.watch('permissions.canManageAllDocs');

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      centered
      title={t('organisationView.boards.members.permissionsModal.title')}
      size="md"
    >
      <Form form={form} onSubmit={onSubmit}>
        <Flex
          sx={theme => ({
            border: `1px solid ${theme.colors.gray[1]}`,
            borderRadius: theme.radius.sm,
            padding: theme.spacing.md,
          })}
          align="center"
          gap="md"
        >
          <UserAvatar
            name={user.name}
            surname={user.surname}
            avatar={user.avatarUrl}
          />
          <Text>{userLabel}</Text>
        </Flex>
        <Stack py={24} spacing="xs">
          <CheckboxField
            name="permissions.canFullTextEdit"
            label={t('boardPermissions.canFullTextEdit')}
          />
          <CheckboxField
            name="permissions.canCreateTemplate"
            label={t('boardPermissions.canCreateTemplate')}
          />
          <CheckboxField
            {...(canManageAllDocsEnabled ? { checked: true } : {})}
            disabled={canManageAllDocsEnabled}
            name="permissions.canSeeOtherDocuments"
            label={t('boardPermissions.canSeeOtherDocuments')}
          />
          <CheckboxField
            name="permissions.canSeeOtherCompanies"
            label={t('boardPermissions.canSeeOtherCompanies')}
          />
          <CheckboxField
            name="permissions.canDeleteArchived"
            label={t('boardPermissions.canDeleteArchived')}
          />
          <CheckboxField
            {...(canManageAllDocsEnabled ? { checked: true } : {})}
            disabled={canManageAllDocsEnabled}
            name="permissions.canAddExternalComments"
            label={t('boardPermissions.canAddExternalComments')}
          />
          <CheckboxField
            {...(canManageAllDocsEnabled ? { checked: true } : {})}
            disabled={canManageAllDocsEnabled}
            name="permissions.canManageManagers"
            label={t('boardPermissions.canManageManagers')}
          />
          <CheckboxField
            name="permissions.canManageAllDocs"
            label={t('boardPermissions.canManageAllDocs')}
          />
        </Stack>
        <Flex justify="flex-end" gap={16}>
          <Button variant="default" onClick={onClose}>
            {t('organisationView.boards.members.permissionsModal.cancelButton')}
          </Button>
          <Button type="submit" loading={form.formState.isSubmitting}>
            {t('organisationView.boards.members.permissionsModal.saveButton')}
          </Button>
        </Flex>
      </Form>
    </Modal>
  );
};
